import { useCallback, useEffect } from 'react';

import { useStore } from '../../../pages/workspace/hooks';
import {
  isAutopositioningInProgress as checkIsAutopositioningInProgress,
  isCalculationStarted as checkIsCalculationStarted,
} from '../../../utils/jobStatus';
import { getForward, getRight } from './cameraOrientationStore';

const STEP = 1; // 1 meter per arrow key press

/**
 * Keyboard shortcut hook: move the selected lamp with arrow keys.
 *
 * Movement is **camera-relative** — "up" always means visually forward
 * regardless of how the scene is rotated.
 *
 * Arrow Up / Down     → forward / back relative to camera view (XY plane)
 * Arrow Left / Right  → left / right relative to camera view (XY plane)
 * Shift + Arrow Up    → Z axis (up)
 * Shift + Arrow Down  → Z axis (down)
 *
 * @param enabled - When false, the hook is disabled and won't capture any keys
 */
export function useArrowKeyMovement(enabled: boolean = true) {
  const { lamps, updateLampPosition, activeVersion, activeToolbarTool } = useStore();

  const selectedLamp = lamps.find((l) => l.isSelected);

  const isCalculationStarted = checkIsCalculationStarted(activeVersion);
  const isAutopositioningInProgress = checkIsAutopositioningInProgress(activeVersion);
  const canModify = !isCalculationStarted && !isAutopositioningInProgress;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Skip if hook is disabled
      if (!enabled) return;

      if (!selectedLamp || !canModify) return;

      // Don't capture keys when user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // Don't capture when measurement tools are active
      if (activeToolbarTool === 'measurement' || activeToolbarTool === 'lampDistance') return;

      // Only handle arrow keys
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;

      // Don't interfere with Cmd/Ctrl combos (undo/redo etc.)
      if (e.metaKey || e.ctrlKey) return;

      e.preventDefault();

      const [x, y, z] = selectedLamp.position;
      let newPosition: [number, number, number];

      if (e.shiftKey) {
        // Shift + Arrow Up/Down → Z axis
        if (e.key === 'ArrowUp') {
          newPosition = [x, y, z + STEP];
        } else if (e.key === 'ArrowDown') {
          newPosition = [x, y, z - STEP];
        } else {
          return; // Shift + Left/Right: no action
        }
      } else {
        // Camera-relative movement on XY plane
        const fwd = getForward();
        const rgt = getRight();

        let dx = 0;
        let dy = 0;

        switch (e.key) {
          case 'ArrowUp':
            dx = fwd[0] * STEP;
            dy = fwd[1] * STEP;
            break;
          case 'ArrowDown':
            dx = -fwd[0] * STEP;
            dy = -fwd[1] * STEP;
            break;
          case 'ArrowRight':
            dx = rgt[0] * STEP;
            dy = rgt[1] * STEP;
            break;
          case 'ArrowLeft':
            dx = -rgt[0] * STEP;
            dy = -rgt[1] * STEP;
            break;
          default:
            return;
        }

        // Round to 2 decimal places to avoid floating-point noise
        newPosition = [Math.round((x + dx) * 100) / 100, Math.round((y + dy) * 100) / 100, z];
      }

      updateLampPosition(selectedLamp.id, newPosition, selectedLamp.position);
    },
    [selectedLamp, canModify, activeToolbarTool, updateLampPosition, enabled],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
